import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import ModuleDetailStore from "./ModuleDetailStore";

/**
 * The Module 360 detail hub — everything HQ knows about ONE governed catalog
 * entry (a module, tool, integration, or a listed platform entry).
 *
 * IDENTITY: `:slug` is the Coordinator `governedSlug`, which is what every
 * module-scoped table joins on and what the catalog list navigates with
 * (`routerStore.goTo("ModuleDetail", { slug })`). It is NOT the `module_catalog`
 * primary key — the store resolves the row with a find on `moduleKey`.
 *
 * `:tab?` IS VESTIGIAL. Tabs deep-link through the query param `?tabName=`
 * (the library `<Tabs useRouterState>` writes it); the path segment is kept
 * only so older/pasted URLs of the form `/modules/quality-control/overview`
 * still resolve. Nothing reads it — do not build logic on it.
 *
 * ONE ROUTE, EVERY KIND: modules, tools and integrations are served
 * near-identically here; `kind` is a facet on the row, not a different page.
 */
const moduleDetailRoute: RouteType[] = [
  {
    name: "ModuleDetail",
    path: "/modules/:slug/:tab?",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        // The STATIC title stays "Module Detail" — routeDisplayTitle (omnibar /
        // left-nav) reads viewportCard.header.title. The open entry's name and
        // governed identity chips are rendered by the header COMPONENT, which
        // cascades into the toolbar right after this title.
        title: "Module Detail",
        enableRefreshButton: true,
        component: () => import("./ModuleDetail.Header"),
      },
      body: {
        component: () => import("./ModuleDetail.Body"),
        grayBackground: true,
      },
    },
    // Parameterized route — hidden from the nav tree (the catalog list owns the
    // "Modules" nav entry).
    group: "Modules",
    hidden: true,
    // RBAC seam: reads require authentication only — viewers browse the
    // governed catalog. The API enforces the operator-or-admin gate on the
    // ownership writes this page offers, and refuses external writes to the
    // sync-owned tables (`module-governance`, `module-releases`) outright.
    requiresAuth: true,
    requiresPermission: false,
    onEnter: ({ rootStore, params }) => {
      const store = rootStore.moduleDetailStore as ModuleDetailStore;
      const slug = params.slug as string;
      if (slug) {
        store.setSlug(slug);
        void store.loadModule();
      }
    },
    // Guarded on IDENTITY, not on params: a tab switch rewrites the query
    // string and fires this hook, and an unguarded reload would refetch the
    // whole page — including the derived services — on every tab click.
    onParamsChange: ({ rootStore, params }) => {
      const store = rootStore.moduleDetailStore as ModuleDetailStore;
      const slug = params.slug as string;
      if (slug && store.slug !== slug) {
        store.setSlug(slug);
        void store.loadModule();
      }
    },
    // Tear down the live module-event subscription (shared refcounted socket —
    // see ../modulesRealtime.ts) when leaving the detail view.
    onExit: ({ rootStore }) => {
      const store = rootStore.moduleDetailStore as ModuleDetailStore;
      store.disposeRealtime();
    },
    dialogs: [
      {
        // Add / edit a module owner (Team tab). One of only two write dialogs
        // on this page (the other closes an action item): everything else here
        // is sync-owned and the API refuses external writes to it. The store
        // re-checks the operator tier inside the
        // mutator and writes through the RAW feathers service so a rejected
        // save surfaces instead of resolving undefined.
        name: "ModuleOwnerFormDialog",
        title: "Module Owner",
        submitButtonText: "Save owner",
        component: () => import("./dialogs/ModuleOwnerForm.Body"),
        cancelButton: true,
        height: 520,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused save throws, so the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          const store = rootStore.moduleDetailStore as ModuleDetailStore;
          throwIfFailed(await store.saveOwner(), store.ownerError, "The owner could not be saved.");
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.moduleDetailStore as ModuleDetailStore;
          return store.isOwnerFormValid && store.canWrite;
        },
      },
      {
        // Close ONE meeting action item attributed to this entry, with a
        // reason. The second write on this page, and the reason the docblock
        // above no longer reads "the ONLY write dialog": an action item is not
        // a sync-owned row — a human closes it.
        name: "ModuleCloseActionItemDialog",
        title: "Close action item",
        component: () => import("./dialogs/CloseActionItem.Body"),
        cancelButton: true,
        submitButtonText: "Close",
        height: 480,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          // A refused close throws, so the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          const store = rootStore.moduleDetailStore as ModuleDetailStore;
          throwIfFailed(
            await store.closeActionItem(),
            store.actionItemCloseError,
            "The action item could not be closed."
          );
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const store = rootStore.moduleDetailStore as ModuleDetailStore;
          return store.isActionItemCloseFormValid && store.canWrite;
        },
      },
    ],
  },
];

export default moduleDetailRoute;
